import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ClipboardList, Clock, Calendar, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentTests = () => {
  const { token } = useAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/test-series`);
        setTests(response.data);
      } catch (error) {
        console.error('Error fetching tests:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTests();
  }, [token]);

  const today = new Date();
  const upcomingTests = tests.filter(t => new Date(t.scheduled_date) >= today);
  const pastTests = tests.filter(t => new Date(t.scheduled_date) < today);

  return (
    <DashboardLayout title="Test Series">
      {/* Upcoming Tests */}
      <div className="mb-8">
        <h3 className="font-heading font-semibold text-lg mb-4">Upcoming Tests</h3>
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="h-24"></CardContent>
              </Card>
            ))}
          </div>
        ) : upcomingTests.length > 0 ? (
          <div className="space-y-4">
            {upcomingTests.map((test) => (
              <Card key={test.id} className="card-hover" data-testid={`test-${test.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                      <ClipboardList className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{test.title}</h4>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{test.scheduled_date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{test.duration_minutes} mins</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Award className="w-4 h-4" />
                          <span>{test.total_marks} marks</span>
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Upcoming</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No upcoming tests scheduled.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Past Tests */}
      <div>
        <h3 className="font-heading font-semibold text-lg mb-4">Past Tests</h3>
        {pastTests.length > 0 ? (
          <div className="space-y-4">
            {pastTests.map((test) => (
              <Card key={test.id} className="card-hover opacity-70">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <ClipboardList className="w-6 h-6 text-slate-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{test.title}</h4>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                        <span>{test.scheduled_date}</span>
                        <span>{test.duration_minutes} mins</span>
                        <span>{test.total_marks} marks</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href="/dashboard/results">View Results</a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No past tests.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StudentTests;
